import { memo } from "react";
import PropTypes from "prop-types";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import useDocumentTitle from "../hooks/useDocumentTitle";
import useImagePreloader from "../hooks/useImagePreloader";
import LoadingOverlay from "../components/LoadingOverlay";

const TextParagraph = memo(({ text, className = "mb-4" }) => (
  <p className={className}>{text}</p>
));

TextParagraph.displayName = "TextParagraph";

TextParagraph.propTypes = {
  text: PropTypes.string.isRequired,
  className: PropTypes.string,
};

const ListItems = memo(({ items, className = "list-disc pl-6 space-y-2" }) => (
  <ul className={className}>
    {items.map((item, idx) => (
      <li key={idx}>{item}</li>
    ))}
  </ul>
));

ListItems.displayName = "ListItems";

ListItems.propTypes = {
  items: PropTypes.arrayOf(PropTypes.string).isRequired,
  className: PropTypes.string,
};

const KeyValueListItems = memo(
  ({ items, className = "list-disc pl-6 space-y-2" }) => (
    <ul className={className}>
      {items.map((item, idx) => (
        <li key={idx}>
          <strong>{item.key}:</strong> {item.value}
        </li>
      ))}
    </ul>
  )
);

KeyValueListItems.displayName = "KeyValueListItems";

KeyValueListItems.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ).isRequired,
  className: PropTypes.string,
};

const SectionTitle = memo(({ title }) => (
  <h2 className="text-xl font-semibold text-[#009fd6] mb-4">{title}</h2>
));

SectionTitle.displayName = "SectionTitle";

SectionTitle.propTypes = {
  title: PropTypes.string.isRequired,
};

const Section = memo(({ title, children }) => (
  <section>
    <SectionTitle title={title} />
    {children}
  </section>
));

Section.displayName = "Section";

Section.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const PrivacyPolicy = memo(() => {
  useDocumentTitle("privacyPolicy");
  const { t } = useTranslation();

  const privacyPolicyData = t("components.privacyPolicy", {
    returnObjects: true,
  });

  const loading = useImagePreloader([]);

  return (
    <>
      {loading && <LoadingOverlay />}
      <div className="bg-black text-gray-200 pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-3xl font-bold text-white mb-8">
              {privacyPolicyData.title}
            </h1>

            <div className="space-y-8">
              {/* 1. Introduction */}
              <Section title={privacyPolicyData.sections.introduction.title}>
                {privacyPolicyData.sections.introduction.content.map(
                  (paragraph, index) => (
                    <TextParagraph key={index} text={paragraph} />
                  )
                )}
              </Section>

              {/* 2. Information We Collect */}
              <Section
                title={privacyPolicyData.sections.informationWeCollect.title}
              >
                {privacyPolicyData.sections.informationWeCollect.content.map(
                  (item, index) =>
                    typeof item === "string" ? (
                      <TextParagraph key={index} text={item} />
                    ) : (
                      <div key={index}>
                        <h3 className="text-lg font-medium text-white mt-4 mb-2">
                          {item.personalInformation.title}
                        </h3>
                        <ListItems
                          items={item.personalInformation.items}
                          className="list-disc pl-6 mb-4 space-y-2"
                        />

                        <h3 className="text-lg font-medium text-white mt-4 mb-2">
                          {item.usageInformation.title}
                        </h3>
                        <ListItems
                          items={item.usageInformation.items}
                          className="list-disc pl-6 mb-4 space-y-2"
                        />

                        <h3 className="text-lg font-medium text-white mt-4 mb-2">
                          {item.cookiesAndSimilarTechnologies.title}
                        </h3>
                        <TextParagraph
                          text={item.cookiesAndSimilarTechnologies.content}
                        />
                      </div>
                    )
                )}
              </Section>

              {/* 3. How We Collect Information */}
              <Section
                title={privacyPolicyData.sections.howWeCollectInformation.title}
              >
                <TextParagraph
                  text={
                    privacyPolicyData.sections.howWeCollectInformation
                      .content[0]
                  }
                />
                <ListItems
                  items={
                    privacyPolicyData.sections.howWeCollectInformation
                      .content[1].items
                  }
                />
              </Section>

              {/* 4. How We Use Your Information */}
              <Section
                title={privacyPolicyData.sections.howWeUseYourInformation.title}
              >
                <TextParagraph
                  text={
                    privacyPolicyData.sections.howWeUseYourInformation
                      .content[0]
                  }
                />
                <ListItems
                  items={
                    privacyPolicyData.sections.howWeUseYourInformation
                      .content[1].items
                  }
                />
              </Section>

              {/* 5. Legal Basis for Processing & Global Compliance */}
              <Section
                title={privacyPolicyData.sections.legalBasisForProcessing.title}
              >
                {privacyPolicyData.sections.legalBasisForProcessing.content.map(
                  (item, index) =>
                    typeof item === "string" ? (
                      <TextParagraph key={index} text={item} />
                    ) : (
                      <KeyValueListItems key={index} items={item.items} />
                    )
                )}
              </Section>

              {/* 6. Data Sharing and Disclosure */}
              <Section
                title={
                  privacyPolicyData.sections.dataSharingAndDisclosure.title
                }
              >
                <TextParagraph
                  text={
                    privacyPolicyData.sections.dataSharingAndDisclosure
                      .content[0]
                  }
                />
                <KeyValueListItems
                  items={
                    privacyPolicyData.sections.dataSharingAndDisclosure
                      .content[1].items
                  }
                />
                <TextParagraph
                  text={
                    privacyPolicyData.sections.dataSharingAndDisclosure
                      .content[2]
                  }
                  className="mt-4"
                />
              </Section>

              {/* 7. Google Analytics */}
              <Section title={privacyPolicyData.sections.googleAnalytics.title}>
                {privacyPolicyData.sections.googleAnalytics.content.map(
                  (paragraph, index) => (
                    <TextParagraph key={index} text={paragraph} />
                  )
                )}
              </Section>

              {/* 8. International Data Transfers */}
              <Section
                title={
                  privacyPolicyData.sections.internationalDataTransfers.title
                }
              >
                {privacyPolicyData.sections.internationalDataTransfers.content.map(
                  (item, index) =>
                    typeof item === "string" ? (
                      <TextParagraph key={index} text={item} />
                    ) : (
                      <ListItems key={index} items={item.items} />
                    )
                )}
              </Section>

              {/* 9. Data Security */}
              <Section title={privacyPolicyData.sections.dataSecurity.title}>
                {privacyPolicyData.sections.dataSecurity.content.map(
                  (item, index) =>
                    typeof item === "string" ? (
                      <TextParagraph key={index} text={item} />
                    ) : (
                      <ListItems key={index} items={item.items} />
                    )
                )}
              </Section>

              {/* 10. Data Retention */}
              <Section title={privacyPolicyData.sections.dataRetention.title}>
                {privacyPolicyData.sections.dataRetention.content.map(
                  (paragraph, index) => (
                    <TextParagraph key={index} text={paragraph} />
                  )
                )}
              </Section>

              {/* 11. Your Data Protection Rights Worldwide */}
              <Section
                title={privacyPolicyData.sections.dataProtectionRights.title}
              >
                {privacyPolicyData.sections.dataProtectionRights.content.map(
                  (item, index) =>
                    typeof item === "string" ? (
                      <TextParagraph key={index} text={item} />
                    ) : (
                      <KeyValueListItems key={index} items={item.items} />
                    )
                )}
              </Section>

              {/* 12. Children's Privacy */}
              <Section
                title={privacyPolicyData.sections.childrensPrivacy.title}
              >
                {privacyPolicyData.sections.childrensPrivacy.content.map(
                  (paragraph, index) => (
                    <TextParagraph key={index} text={paragraph} />
                  )
                )}
              </Section>

              {/* 13. Changes to This Privacy Policy */}
              <Section
                title={privacyPolicyData.sections.changesToPrivacyPolicy.title}
              >
                {privacyPolicyData.sections.changesToPrivacyPolicy.content.map(
                  (paragraph, index) => (
                    <TextParagraph key={index} text={paragraph} />
                  )
                )}
              </Section>

              {/* 14. Contact Us */}
              <Section title={privacyPolicyData.sections.contactUs.title}>
                {privacyPolicyData.sections.contactUs.content.map(
                  (item, index) =>
                    typeof item === "string" ? (
                      <TextParagraph key={index} text={item} />
                    ) : (
                      <div key={index} className="bg-gray-800 p-4 rounded-lg">
                        <TextParagraph
                          text={item.companyName}
                          className="mb-2"
                        />
                        <TextParagraph
                          text={`Email: ${item.email}`}
                          className="mb-2"
                        />
                        <TextParagraph
                          text={`Phone: ${item.phone}`}
                          className="mb-2"
                        />
                        <TextParagraph text={`Address: ${item.address}`} />
                      </div>
                    )
                )}
              </Section>

              {/* 15. Admin Panel & Chat Data */}
              <Section
                title={privacyPolicyData.sections.adminPanelAndChatData.title}
              >
                {privacyPolicyData.sections.adminPanelAndChatData.content.map(
                  (paragraph, index) => (
                    <TextParagraph key={index} text={paragraph} />
                  )
                )}
              </Section>

              {/* Last Updated */}
              <div className="border-t border-gray-700 pt-6 mt-8">
                <TextParagraph
                  text={privacyPolicyData.lastUpdated}
                  className="text-sm text-gray-400"
                />
              </div>
            </div>

            {/* Return to Home Button */}
            <div className="mt-12 flex justify-center">
              <Link to="/">
                <button className="bg-[#009fd6] text-white px-6 py-3 rounded-lg hover:bg-[#007cab] transition-colors">
                  {privacyPolicyData.returnToHome}
                </button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
});

PrivacyPolicy.displayName = "PrivacyPolicy";

export default memo(PrivacyPolicy);
